// Действия с пользователем (contracts/ui.md, «Действия и подтверждения»): архив, сброс пароля,
// завершение сеансов и новая ссылка — через окно подтверждения с последствиями в числах;
// снятие блокировки и возврат из архива — сразу, с тостом «Отменить» (ux.md, раздел 1; T134).
// «Отменить» само тоста не даёт: итог виден на месте (ux.md, раздел 2), а цепочка «Отменить»
// после «Отменить» обошла бы окно подтверждения архива. Тексты не зависят от рода человека
// и согласуют число (FR-036а, FR-036в).
import { formatCount, nouns } from '@kp/contracts';
import type { IssuedInvite, User, UserDetail } from '@kp/contracts';
import { ConfirmDialog, useToast } from '@kp/ui';
import { KeyRound, Link2, LogOut } from '@kp/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { RefObject } from 'react';
import { api, isApiError } from '../../api/client.ts';
import { useErrorToast } from '../../app/errors.ts';
import { formatDate } from '../../app/format.ts';
import { countPhrase, joinPhrases } from '../../app/texts.ts';
import type { VerbForms } from '../../app/texts.ts';
import { InviteLinkModal, inviteValidity } from './invite-link.tsx';

const WILL_END: VerbForms = ['завершится', 'завершатся'];
const WILL_FINISH: VerbForms = ['закончится', 'закончатся'];

function isCurrent(state: string): boolean {
  return state === 'active' || state === 'planned';
}

/**
 * «Завершатся 2 сеанса, закончится 1 замещение, где пользователь — заместитель. Замещение роли
 * пользователя продолжится до 31.10.2026. Роли сохранятся и вернутся вместе с пользователем».
 */
export function archiveDescription(detail: UserDetail): string {
  const asDeputy = detail.substitutions.filter(
    (item) => item.deputy.id === detail.id && isCurrent(item.state),
  ).length;
  const asOwner = detail.substitutions.filter(
    (item) => item.owner.id === detail.id && isCurrent(item.state),
  );
  const consequences = joinPhrases([
    countPhrase(detail.sessions.length, WILL_END, nouns.session),
    asDeputy > 0
      ? `${countPhrase(asDeputy, WILL_FINISH, nouns.substitution)}, где пользователь — заместитель`
      : null,
  ]);
  const parts: string[] = [];
  if (consequences) parts.push(`${consequences}.`);
  const lastDay = asOwner
    .map((item) => item.validTo)
    .sort()
    .at(-1);
  if (lastDay) parts.push(`Замещение роли пользователя продолжится до ${formatDate(lastDay)}.`);
  parts.push('Роли сохранятся и вернутся вместе с пользователем');
  return parts.join(' ');
}

/** «Завершатся 2 сеанса. Блокировка снимется. Новая ссылка будет действовать 72 часа». */
export function resetDescription(detail: UserDetail): string {
  const sessions = joinPhrases([countPhrase(detail.sessions.length, WILL_END, nouns.session)]);
  return [
    sessions ? `${sessions}.` : null,
    detail.state === 'locked' ? 'Блокировка снимется.' : null,
    'Новая ссылка будет действовать 72 часа',
  ]
    .filter(Boolean)
    .join(' ');
}

export type UserDialog = 'archive' | 'reset' | 'revoke' | 'invite';

interface Issued {
  title: string;
  description: string;
  invite: IssuedInvite;
}

/** Окна подтверждения карточки и окно со ссылкой после сброса пароля или выпуска ссылки. */
export function UserActionDialogs({
  detail,
  dialog,
  onClose,
  timeZone,
  returnFocusRef,
}: {
  detail: UserDetail;
  dialog: UserDialog | null;
  onClose: () => void;
  timeZone: string;
  returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  const toast = useToast();
  const showError = useErrorToast();
  const refresh = useRefreshUser();
  const restore = useRestoreUser();
  const [issued, setIssued] = useState<Issued | null>(null);
  const { id, fullName } = detail;

  /** Ошибка: окно закрывается, если повторять бессмысленно (ACC-004, REC-009), иначе остаётся. */
  const fail = (error: unknown, retry: () => void): never => {
    if (isApiError(error, 'ACC-004') || isApiError(error, 'REC-009')) {
      onClose();
      showError(error);
    } else {
      showError(error, retry);
    }
    throw error;
  };

  const archive = async () => {
    try {
      await api('users.archive', { params: { id } });
    } catch (error) {
      fail(error, () => void archive().catch(() => undefined));
    }
    await refresh();
    toast.ok('Пользователь перенесён в архив', {
      action: { label: 'Отменить', onClick: () => void restore(id, { undo: true }) },
    });
  };

  const reset = async () => {
    let invite: IssuedInvite;
    try {
      invite = await api('users.resetPassword', { params: { id } });
    } catch (error) {
      return fail(error, () => void reset().catch(() => undefined));
    }
    await refresh();
    setIssued({
      title: 'Пароль сброшен',
      description: `Перешлите ссылку: по ней ${fullName} задаст новый пароль. ${inviteValidity(invite, timeZone)}`,
      invite,
    });
  };

  const revoke = async () => {
    try {
      await api('users.revokeSessions', { params: { id } });
    } catch (error) {
      fail(error, () => void revoke().catch(() => undefined));
    }
    await refresh();
    toast.ok('Сеансы завершены');
  };

  const issue = async () => {
    let invite: IssuedInvite;
    try {
      invite = await api('users.invite', { params: { id } });
    } catch (error) {
      return fail(error, () => void issue().catch(() => undefined));
    }
    await refresh();
    setIssued({
      title: 'Новая ссылка-приглашение',
      description: `По этой ссылке ${fullName} задаст пароль сам. ${inviteValidity(invite, timeZone)}`,
      invite,
    });
  };

  const sessions = detail.sessions.length;
  const change = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <>
      <ConfirmDialog
        open={dialog === 'archive'}
        onOpenChange={change}
        tone="warn"
        title={`Перенести в архив: ${fullName}?`}
        description={archiveDescription(detail)}
        confirmLabel="Перенести в архив"
        onConfirm={archive}
        returnFocusRef={returnFocusRef}
      />
      <ConfirmDialog
        open={dialog === 'reset'}
        onOpenChange={change}
        tone="warn"
        icon={KeyRound}
        title={`Сбросить пароль: ${fullName}?`}
        description={resetDescription(detail)}
        confirmLabel="Сбросить пароль"
        onConfirm={reset}
        returnFocusRef={returnFocusRef}
      />
      <ConfirmDialog
        open={dialog === 'revoke'}
        onOpenChange={change}
        tone="warn"
        icon={LogOut}
        title={`Завершить ${formatCount(sessions, nouns.session)}: ${fullName}?`}
        description="Войти снова придётся на каждом устройстве"
        confirmLabel="Завершить сеансы"
        onConfirm={revoke}
        returnFocusRef={returnFocusRef}
      />
      <ConfirmDialog
        open={dialog === 'invite'}
        onOpenChange={change}
        tone="warn"
        icon={Link2}
        title={`Выпустить новую ссылку для ${fullName}?`}
        description="Прежняя ссылка перестанет работать"
        confirmLabel="Выпустить ссылку"
        onConfirm={issue}
        returnFocusRef={returnFocusRef}
      />
      <InviteLinkModal
        open={issued !== null}
        onOpenChange={(open) => {
          if (!open) setIssued(null);
        }}
        title={issued?.title ?? ''}
        description={issued?.description ?? ''}
        invite={issued?.invite ?? null}
        returnFocusRef={returnFocusRef}
      />
    </>
  );
}

/** Перечитать карточку и список после действия. */
export function useRefreshUser(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['api', 'users.get'] }),
      queryClient.invalidateQueries({ queryKey: ['api', 'users.list'] }),
    ]);
  }, [queryClient]);
}

export interface RestoreOptions {
  /** «Отменить» в тосте архива: пользователь возвращается без своего тоста — итог виден на месте. */
  undo?: boolean;
}

/**
 * Возврат из архива — сразу, тост «Пользователь возвращён · Отменить» (FR-026б, FR-026в; T134).
 * «Отменить» переносит пользователя обратно в архив без окна: у только что возвращённого нет
 * сеансов и замещений — последствий, которые называло бы окно архива, нет (FR-026в). true —
 * пользователь возвращён.
 */
export function useRestoreUser(): (id: string, options?: RestoreOptions) => Promise<boolean> {
  const toast = useToast();
  const showError = useErrorToast();
  const refresh = useRefreshUser();
  /** «Отменить» возврата: снова в архив; ошибка — тост с причиной и кодом. */
  const archiveBack = useCallback(
    async function archiveBack(id: string): Promise<void> {
      try {
        await api('users.archive', { params: { id } });
      } catch (error) {
        showError(error, () => void archiveBack(id));
      } finally {
        await refresh();
      }
    },
    [showError, refresh],
  );
  return useCallback(
    async function restore(id: string, options: RestoreOptions = {}): Promise<boolean> {
      try {
        await api('users.restore', { params: { id } });
      } catch (error) {
        showError(error, () => void restore(id, options));
        return false;
      }
      await refresh();
      if (!options.undo) {
        toast.ok('Пользователь возвращён', {
          action: { label: 'Отменить', onClick: () => void archiveBack(id) },
        });
      }
      return true;
    },
    [toast, showError, refresh, archiveBack],
  );
}

/**
 * Отказ «Отменить» снятия блокировки по состоянию записи: запись изменили после снятия (REC-001)
 * или пользователя перенесли в архив (REC-009). Тот же запрос получит тот же отказ — тост без
 * «Повторить»; прочие сбои (нет связи, сбой сервера) — с «Повторить» той же отмены (T155).
 */
export function isUnlockUndoRefusal(error: unknown): boolean {
  return isApiError(error, 'REC-001') || isApiError(error, 'REC-009');
}

/**
 * Снять блокировку — сразу, тост «Блокировка снята · Отменить» (FR-027а; T134). «Отменить»
 * возвращает блокировку до прежнего срока (POST /api/users/{id}/unlock/undo) с версией из ответа
 * снятия: если запись успели изменить — отказ REC-001 тостом, блокировка не возвращается. Отмена
 * не сохранилась — тост-ошибка с «Повторить» той же отмены (ux.md, раздел 2; T155).
 */
export function useUnlockUser(): (id: string) => Promise<void> {
  const toast = useToast();
  const showError = useErrorToast();
  const refresh = useRefreshUser();
  const undoUnlock = useCallback(
    async function undoUnlock(user: User): Promise<void> {
      try {
        await api('users.unlockUndo', { params: { id: user.id }, version: user.version });
      } catch (error) {
        showError(error, isUnlockUndoRefusal(error) ? undefined : () => void undoUnlock(user));
      } finally {
        await refresh();
      }
    },
    [showError, refresh],
  );
  return useCallback(
    async function unlock(id: string): Promise<void> {
      let unlocked: User;
      try {
        unlocked = await api('users.unlock', { params: { id } });
      } catch (error) {
        showError(error, () => void unlock(id));
        return;
      }
      await refresh();
      toast.ok('Блокировка снята', {
        action: { label: 'Отменить', onClick: () => void undoUnlock(unlocked) },
      });
    },
    [toast, showError, refresh, undoUnlock],
  );
}
